import { FC, ReactNode, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { CloudProvider } from "@/services/cloudchipr.api";
import { InfoDataChip } from "@/components/pages/dashboard/components/widgets/common/widget-header/source-and-name/InfoDataChip";
import { ProviderIcon } from "@/components/common/ProviderIcon";
import { getProviderName } from "@/utils/helpers/providers/getProviderName";
import { useAppSelector } from "@/store/hooks";
import { commitmentsOrganizationsSelector } from "@/store/commitments/selectors/organisations/commitmentsOrganizationsSelector";

interface OrgDataChipProps {
  provider?: CloudProvider;
  providerOrganisationId?: string;
  children?: ReactNode;
}

export const OrgDataChip: FC<OrgDataChipProps> = ({
  provider,
  children,
  providerOrganisationId,
}) => {
  const providerTitle = getProviderName(provider ?? "aws", { title: true });
  const title = providerOrganisationId ?? providerTitle;

  const commitmentOrganizations = useAppSelector(
    commitmentsOrganizationsSelector,
  );

  const isSingle = useMemo(() => {
    const currentProviderOrgs = commitmentOrganizations?.filter(
      (org) => org.cloud_provider === provider,
    );

    return (
      currentProviderOrgs?.length === 1 &&
      currentProviderOrgs?.at(0)?.organization_id === providerOrganisationId
    );
  }, [commitmentOrganizations, provider, providerOrganisationId]);

  if (!provider && !providerOrganisationId) {
    return null;
  }

  return (
    <InfoDataChip>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="inherit">Org ID: </Typography>

        {provider && <ProviderIcon provider={provider} width="16" />}
        <Typography
          component="span"
          variant="inherit"
          fontWeight="medium"
          color="text.primary"
        >
          {isSingle ? providerTitle : title}
        </Typography>

        {children}
      </Stack>
    </InfoDataChip>
  );
};
